import Axios from "axios"
import i18n from "i18n/index"

import store from "store"
import { config } from "./config.helper"
import { LocalStorageHelper, LocalStorageKey } from "./localStorage.helper"

Axios.defaults.baseURL = config.appApi
export const axios = Axios.create()

let isRefreshing = false
let subscribers = []

axios.interceptors.request.use(config => {
    const token = LocalStorageHelper.getItem(LocalStorageKey.Token)
    if (token !== null && config.headers) {
        config.headers.authorization = `Bearer ${token}`
    }

    const baseURL = LocalStorageHelper.getItem(LocalStorageKey.BaseUrl)
    if (baseURL) {
        config.baseURL = baseURL
    }

    return config
})

axios.interceptors.response.use(
    response => response,
    async error => {
        const { response, config } = error

        if (response?.status !== 401 || config.url?.includes("/auth/login")) {
            return Promise.reject(error)
        }

        const updatedConfig = new Promise((resolve, reject) => {
            // Replace the expired token and store request.
            subscribers.push({
                resolve: accessToken => {
                    config.headers.authorization = `Bearer ${accessToken}`
                    return resolve(axios(response.config))
                },
                reject: error => {
                    reject(error)
                }
            })
        })

        if (!isRefreshing) {
            isRefreshing = true
            const refreshToken = LocalStorageHelper.getItem(
                LocalStorageKey.RefreshToken
            )

            try {
                const refreshTokenResponse = await Axios.post(
                    "/api/auth/refreshToken",
                    { refreshToken }
                )
                const {
                    token,
                    refreshToken: newRefreshToken
                } = refreshTokenResponse.data

                LocalStorageHelper.setItem(LocalStorageKey.Token, token)
                LocalStorageHelper.setItem(
                    LocalStorageKey.RefreshToken,
                    newRefreshToken
                )

                // Retry all subscribers with new accesstoken.
                subscribers.forEach(subscriber => subscriber.resolve(token))
            } catch (error) {
                if (Axios.isAxiosError(error)) {
                    await logout()
                    window.location.reload()
                    return
                }

                subscribers.forEach(subscriber => subscriber.reject(error))
            } finally {
                isRefreshing = false
                subscribers = []
            }
        }

        return updatedConfig
    }
)

function parseError(error) {
    const { errors, message } = error.response?.data
    if (!errors) {
        return typeof message === "string" ? message : {}
    }

    const fields = Object.keys(errors)
    return Object.fromEntries(
        fields.map(field => [field, getErrorMessage(errors[field])])
    )
}

function getErrorMessage(data) {
    let errorKey = typeof data.msg === "string" ? data.msg : data.msg.type
    errorKey = errorKey.charAt(0).toLowerCase() + errorKey.slice(1)

    if (typeof data.msg === "string" && !i18n.exists(`errors.${errorKey}`)) {
        return camelCaseToSentenceCase(errorKey)
    }

    return i18n.t(`errors.${errorKey}`, {
        ...data,
        param: camelCaseToSentenceCase(data.param)
    })
}

export function camelCaseToSentenceCase(text) {
    const separatedText = text.replace(/([A-Z])/g, " $1")
    return (
        separatedText.charAt(0).toUpperCase() + separatedText.slice(1).toLowerCase()
    )
}

export function logout() {
    return new Promise(async resolve => {
        try {
            const refreshToken = LocalStorageHelper.getItem(
                LocalStorageKey.RefreshToken
            )
            await Axios.post("/api/auth/logout", { refreshToken })
        } finally {
            Object.values(LocalStorageKey).forEach(key => {
                LocalStorageHelper.removeItem(key)
            })

            // for backward compatibility
            store.dispatch({ type: "SET_TOKEN", payload: "" })
            store.dispatch({ type: "USER_LOGOUT" })
            resolve()
        }
    })
}

export default axios
export const AxiosHelper = { parseError, logout }
